import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, Workflow, FileCheck, Award, TrendingUp, Shield, Code, Layers, Settings, Headphones } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Home = () => {
  const benefits = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Specialized Print Industry Knowledge',
      description: 'Professionals who understand print production environments and graphic arts processes'
    },
    {
      icon: <Headphones className="w-8 h-8" />,
      title: 'Customer Service Focused',
      description: 'Dedicated CSR professionals trained in print industry customer communication'
    },
    {
      icon: <Layers className="w-8 h-8" />,
      title: 'Graphic Arts Expertise',
      description: 'Trained in artwork review, file preparation, and print-ready verification'
    },
    {
      icon: <Workflow className="w-8 h-8" />,
      title: 'Workflow-Oriented Support',
      description: 'Understanding of print production workflows and operational processes'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Flexible Scaling',
      description: 'Support that grows with your business needs and production demands'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Reliable Remote Staffing',
      description: 'Dedicated professionals committed to operational excellence'
    }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden">
        <div className="hero-gradient absolute inset-0 opacity-10"></div>
        <div className="container mx-auto px-6 py-24 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="hero-content space-y-8">
              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight">
                Specialized <span className="text-gradient">CSR & Graphic Production Support</span> for Print Companies
              </h1>
              <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed">
                We help print shops, sign companies, and graphic production businesses streamline customer service, file management, artwork preparation, and operational workflows with specialized remote professionals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="btn-primary text-lg px-8 py-6 group">
                    Schedule a Free Consultation
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href="mailto:contact@latintalentremote.com">
                  <Button size="lg" variant="outline" className="btn-secondary text-lg px-8 py-6">
                    Talk to a Specialist
                  </Button>
                </a>
              </div>
            </div>
            <div className="hero-image relative">
              <div className="image-wrapper rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src="https://images.pexels.com/photos/36733304/pexels-photo-36733304.jpeg"
                  alt="Print production professionals"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Built for the Printing Industry</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              Latin Talent Remote was created to support printing and graphic production companies with specialized remote professionals who understand the industry's operational and technical requirements.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed">
              Our focus is helping businesses improve efficiency through dedicated support in customer service, file preparation, artwork review, and order processing.
            </p>
            <div className="bg-gradient-to-r from-cyan-50 to-green-50 p-8 rounded-2xl mt-8">
              <p className="text-2xl font-bold text-gray-900 mb-2">We are not a generic virtual assistant agency.</p>
              <p className="text-xl text-gray-700">We specialize in supporting print production environments.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Expertise Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Industry Knowledge That Makes a Difference</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Our specialists are familiar with the software, workflows, and processes that matter to your business
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="icon-wrapper w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center mb-6 text-white mx-auto">
                <Layers className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-center">Adobe Creative Cloud</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Adobe Illustrator</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Adobe Photoshop</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Adobe InDesign</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Adobe Acrobat Pro</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="icon-wrapper w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center mb-6 text-white mx-auto">
                <FileCheck className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-center">Prepress & File Preparation</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>PDF Review</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Print Ready File Preparation</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Bleeds and Safe Areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Resolution Verification</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Basic Preflight Procedures</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="icon-wrapper w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center mb-6 text-white mx-auto">
                <Settings className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-center">Production Support</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Order Processing</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Production Tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Job Ticket Creation</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Workflow Coordination</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>File Management</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Why Print Companies Work With Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized support that understands your business and operational needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="benefit-card p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="icon-wrapper w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center mb-6 text-white">
                  {benefit.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Specialized Print Industry Support</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Dedicated professionals who understand print production environments
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Customer Service Representatives (CSR)',
                description: 'Dedicated professionals who manage customer communications, order intake, quotation requests, production updates, and workflow coordination',
                image: 'https://images.unsplash.com/photo-1626863905121-3b0c0ed7b94c',
                icon: <Headphones className="w-8 h-8" />
              },
              {
                title: 'Graphic Production Specialists',
                description: 'Professionals trained in artwork review, file preparation, print-ready verification, prepress support, and production documentation',
                image: 'https://images.unsplash.com/photo-1516387938699-a93567ec168e',
                icon: <Layers className="w-8 h-8" />
              },
              {
                title: 'Order Processing & File Management',
                description: 'Support for receiving files, organizing customer assets, generating production orders, tracking projects, and maintaining workflow accuracy',
                image: 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a',
                icon: <FileCheck className="w-8 h-8" />
              }
            ].map((service, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center mb-4 text-white">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link to="/services" className="text-cyan-600 hover:text-cyan-700 font-semibold flex items-center gap-2">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Software Solutions Section */}
      <section className="py-24 bg-gradient-to-br from-cyan-50 to-green-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="icon-wrapper w-20 h-20 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center mb-6 text-white mx-auto">
                <Code className="w-10 h-10" />
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">Optional Software Development</h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                For companies looking to improve operational efficiency, we can develop custom solutions tailored to print production workflows.
              </p>
            </div>
            
            <Card className="p-8">
              <h3 className="text-2xl font-bold mb-6 text-center">Examples of Custom Solutions:</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-700">Order Intake Systems</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-700">Customer Portals</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-700">Production Tracking Platforms</span>
                  </li>
                </ul>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-700">File Upload Systems</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-700">Job Ticket Management Tools</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-700">Workflow Automation Solutions</span>
                  </li>
                </ul>
              </div>
              <p className="text-center text-gray-600 mt-6 italic">
                This service is optional and developed according to each client's requirements.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Built for Graphic Production Businesses</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized support across all print and graphic production sectors
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { name: 'Commercial Printing', image: 'https://images.unsplash.com/photo-1503694978374-8a2fa686963a' },
              { name: 'Large Format Printing', image: 'https://images.unsplash.com/photo-1693031630369-bd429a57f115' },
              { name: 'Sign Shops', image: 'https://images.unsplash.com/photo-1773525912476-213bff96b8a4' },
              { name: 'Packaging Printing', image: 'https://images.unsplash.com/photo-1597765654576-f23f48b956ad' },
              { name: 'Promotional Products', image: 'https://images.pexels.com/photos/7709276/pexels-photo-7709276.jpeg' },
              { name: 'Graphic Production Facilities', image: 'https://images.pexels.com/photos/8875503/pexels-photo-8875503.jpeg' },
              { name: 'Print Brokers', image: 'https://images.unsplash.com/photo-1516387938699-a93567ec168e' }
            ].map((industry, index) => (
              <Card key={index} className="group cursor-pointer overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={industry.image}
                    alt={industry.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end">
                    <p className="text-white font-bold p-4 text-sm">{industry.name}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/industries">
              <Button size="lg" className="btn-primary">
                View All Industries
                <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple process to get specialized support for your operation
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { step: '1', title: 'Schedule a Consultation', desc: 'Tell us about your operation and challenges' },
              { step: '2', title: 'Needs Assessment', desc: 'We identify the best support profile for your business' },
              { step: '3', title: 'Candidate Presentation', desc: 'Meet and evaluate your assigned specialist' },
              { step: '4', title: 'Integration', desc: 'Your new team member integrates into your workflow' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/process">
              <Button size="lg" variant="outline" className="btn-secondary">
                See Detailed Process
                <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section py-24 bg-gradient-to-r from-cyan-500 to-green-500 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">Ready to Improve Your Operation?</h2>
          <p className="text-xl lg:text-2xl mb-10 opacity-90 max-w-3xl mx-auto">
            Schedule a free consultation and discover how specialized remote support can strengthen your print business
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-10 py-6 font-semibold">
                Schedule Free Consultation
                <ArrowRight className="ml-2" />
              </Button>
            </Link>
            <a href="mailto:contact@latintalentremote.com">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cyan-600 text-lg px-10 py-6 font-semibold">
                Email Us Directly
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
